<script setup>
/**
 * FormControlMBasketOrdersLogs
 * Evaluates whether an order possesses system log histories.
 * If true, renders an ignition action button to spawn the log interaction modal.
 */
import {ref, watch, onMounted, computed} from 'vue'
import Button from '@/common/Button.vue'
import {useOrderLogsStore} from '@/stores/orderLogs'
import {useModalsStore} from '@/stores/modals'
import {useAppStore} from "@/stores/appStore"

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
})

const appStore = useAppStore()
const orderLogsStore = useOrderLogsStore()
const modalsStore = useModalsStore()

const show = ref(false)

const betaMode = computed(() => appStore.betaMode)

/**
 * Checks the central store array of logged order indices
 * to see if this specific entity has entries.
 */
const evaluateLogStatus = async () => {
    const orderId = props.currentData?.id

    if (!orderId || !betaMode.value) {
        show.value = false
        props.field.hidden = true
        return
    }

    const loggedOrderIds = await orderLogsStore.fetchOrderIds() || []
    show.value = loggedOrderIds.includes(orderId)
}

/**
 * Triggers the operational store overlay system modal.
 */
const openLogsModal = () => {
    modalsStore.show({
        componentName: 'OrdersLogsModal',
        obj: {orderId: props.currentData.id}
    })
}

/**
 * Syncs the visibility configuration with the parent form group orchestrator.
 */
watch(show, (shouldShow) => {
    props.field.hidden = !shouldShow
})

/**
 * Reactively monitors order record transitions (handles moving across rows or hitting /0)
 */
watch(() => props.currentData?.id, () => {
    evaluateLogStatus()
})

onMounted(() => {
    evaluateLogStatus()
})
</script>

<template>
    <div class="d-inline-block">
        <Button
            v-if="show"
            default-class="btn-light btn-sm border text-nowrap"
            @click="openLogsModal"
        >
            <i class="bi bi-journal-text me-1"></i> Open logs
        </Button>
    </div>
</template>